// API Plans and Rate Limiting
export enum PlanType {
  FREE = 'FREE',
  STARTER = 'STARTER',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE'
}

export interface RateLimits {
  requests: {
    perMinute: number;
    perHour: number;
    perDay: number;
  };
  capsules: {
    maxPerMonth: number;
    maxStorage: number; // in MB
  };
  features: {
    encryption: boolean;
    multiSignature: boolean;
    advancedAnalytics: boolean;
    customBranding: boolean;
    prioritySupport: boolean;
    webhooks: boolean;
  };
}

export interface RateLimitInfo {
  limit: number;
  remaining: number;
  reset: number;
  retryAfter?: number;
}

export const PLAN_LIMITS: Record<PlanType, RateLimits> = {
  [PlanType.FREE]: {
    requests: {
      perMinute: 10,
      perHour: 100,
      perDay: 500
    },
    capsules: {
      maxPerMonth: 5,
      maxStorage: 50
    },
    features: {
      encryption: true,
      multiSignature: false,
      advancedAnalytics: false,
      customBranding: false,
      prioritySupport: false,
      webhooks: false
    }
  },
  [PlanType.STARTER]: {
    requests: {
      perMinute: 30,
      perHour: 500,
      perDay: 5000
    },
    capsules: {
      maxPerMonth: 50,
      maxStorage: 500
    },
    features: {
      encryption: true,
      multiSignature: true,
      advancedAnalytics: false,
      customBranding: false,
      prioritySupport: false,
      webhooks: true
    }
  },
  [PlanType.PRO]: {
    requests: {
      perMinute: 100,
      perHour: 2000,
      perDay: 20000
    },
    capsules: {
      maxPerMonth: 500,
      maxStorage: 5000
    },
    features: {
      encryption: true,
      multiSignature: true,
      advancedAnalytics: true,
      customBranding: true,
      prioritySupport: true,
      webhooks: true
    }
  },
  [PlanType.ENTERPRISE]: {
    requests: {
      perMinute: -1, // unlimited
      perHour: -1,
      perDay: -1
    },
    capsules: {
      maxPerMonth: -1,
      maxStorage: -1
    },
    features: {
      encryption: true,
      multiSignature: true,
      advancedAnalytics: true,
      customBranding: true,
      prioritySupport: true,
      webhooks: true
    }
  }
};

// SDK Configuration
export interface SDKConfig {
  apiKey: string;
  network: 'mainnet' | 'testnet' | 'devnet';
  rpcUrl?: string;
  ipfsGateway?: string;
  pinataJwt?: string;
  debug?: boolean;
  contracts?: {
    timeCapsule?: string;
    legalDocuments?: string;
    multiSig?: string;
    payments?: string;
    specializedCapsules?: string;
    zkmlVerifier?: string;
  };
  cache?: {
    enabled: boolean;
    ttl?: number; // seconds
  };
  retry?: {
    attempts: number;
    delay: number; // milliseconds
  };
}

// Time Capsule Types
export interface TimeCapsule {
  id: string;
  ipfsHash: string;
  unlockTime: Date;
  authorizedAddresses: string[];
  isUnlocked: boolean;
  metadata?: CapsuleMetadata;
  createdAt: Date;
  updatedAt: Date;
}

export interface CapsuleMetadata {
  title: string;
  description: string;
  type: 'text' | 'file' | 'mixed';
  encrypted: boolean;
  fileCount?: number;
  totalSize?: number; // bytes
  tags?: string[];
}

export interface CreateCapsuleInput {
  title: string;
  description: string;
  content: string | File | File[];
  unlockTime: Date;
  authorizedAddresses?: string[];
  encrypt?: boolean;
  password?: string;
  metadata?: Record<string, any>;
}

// Payment Capsule Types
export interface PaymentCapsule {
  id: string;
  sender: string;
  recipient: string;
  amount: string;
  token: 'ETH' | 'USDC' | 'USDT' | 'DAI';
  unlockTime: Date;
  status: 'pending' | 'claimable' | 'claimed' | 'refunded';
  message?: string;
  encrypted?: boolean;
  createdAt: Date;
  claimedAt?: Date;
}

export interface CreatePaymentInput {
  recipient: string;
  amount: string;
  token: 'ETH' | 'USDC' | 'USDT' | 'DAI';
  unlockTime: Date;
  message?: string;
  encrypt?: boolean;
  password?: string;
}

// Legal Document Types
export interface LegalDocument {
  id: string;
  documentHash: string;
  documentType: 'NDA' | 'CONTRACT' | 'WILL' | 'AGREEMENT' | 'CERTIFICATE';
  creator: string;
  signers: SignerInfo[];
  requiredSignatures: number;
  status: 'draft' | 'pending' | 'active' | 'expired' | 'destroyed';
  effectiveDate: Date;
  expiryDate?: Date;
  selfDestruct?: SelfDestructConfig;
  metadata?: LegalMetadata;
  createdAt: Date;
  updatedAt: Date;
}

export interface SignerInfo {
  address: string;
  signed: boolean;
  signature?: string;
  signedAt?: Date;
  name?: string;
  email?: string;
}

export interface SelfDestructConfig {
  type: 'time' | 'views' | 'manual';
  value?: number | Date;
  currentViews?: number;
}

export interface LegalMetadata {
  title: string;
  parties: string[];
  jurisdiction?: string;
  encrypted: boolean;
  fileType: string;
  fileSize: number;
  checksum: string;
}

export interface CreateLegalDocumentInput {
  file: File;
  documentType: LegalDocument['documentType'];
  signers: string[];
  requiredSignatures?: number;
  effectiveDate?: Date;
  expiryDate?: Date;
  selfDestruct?: SelfDestructConfig;
  encrypt?: boolean;
  password?: string;
  metadata?: Record<string, any>;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  metadata?: {
    timestamp: number;
    requestId: string;
    rateLimit?: {
      limit: number;
      remaining: number;
      reset: number;
    };
  };
}

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

// Events
export interface SDKEvents {
  'capsule:created': (capsule: TimeCapsule) => void;
  'capsule:unlocked': (capsuleId: string) => void;
  'payment:created': (payment: PaymentCapsule) => void;
  'payment:claimed': (paymentId: string) => void;
  'document:created': (document: LegalDocument) => void;
  'document:signed': (documentId: string, signer: string) => void;
  'rateLimit:exceeded': (limit: RateLimits) => void;
  'error': (error: ApiError) => void;
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Filters
export interface CapsuleFilters {
  status?: 'locked' | 'unlocked';
  type?: 'time' | 'payment' | 'legal';
  createdAfter?: Date;
  createdBefore?: Date;
  creator?: string;
  search?: string;
}

// Analytics
export interface AnalyticsData {
  totalCapsules: number;
  totalPayments: number;
  totalDocuments: number;
  storageUsed: number;
  activeUsers: number;
  monthlyGrowth: number;
  topCategories: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
}

// Webhooks
export interface WebhookConfig {
  url: string;
  events: string[];
  secret?: string;
  active: boolean;
}

export interface WebhookPayload {
  event: string;
  data: any;
  timestamp: number;
  signature?: string;
}

// Specialized Capsule Types
export enum SpecializedCapsuleType {
  FILM = 'FILM',
  CREATOR_MESSAGE = 'CREATOR_MESSAGE',
  MYSTERY_TRAIT = 'MYSTERY_TRAIT',
  VIP_ACCESS = 'VIP_ACCESS',
  ART_PROVENANCE = 'ART_PROVENANCE',
  VOTING = 'VOTING',
  MUSIC = 'MUSIC',
  ACHIEVEMENT = 'ACHIEVEMENT',
  EDUCATIONAL = 'EDUCATIONAL',
  MEMORY = 'MEMORY'
}

export interface SpecializedCapsule {
  id: string;
  type: SpecializedCapsuleType;
  owner: string;
  metadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateFilmCapsuleInput {
  recipient: string;
  title: string;
  director: string;
  premiereDate: Date;
  trailerURI: string;
  encryptedFilmURI: string;
  posterURI?: string;
}

export interface CreateCreatorMessageInput {
  recipient: string;
  creator: string;
  message?: string;
  encryptedMessage?: string;
  publicTeaser: string;
  revealTime: Date;
  encrypt?: boolean;
  password?: string;
}

export interface CreateMysteryTraitInput {
  recipient: string;
  name: string;
  hiddenTraits: string[];
  unlockTimes: Date[];
}

export interface CreateVIPAccessInput {
  recipient: string;
  eventId: string;
  description: string;
  encryptedAccessData: string;
  expiryTime: Date;
  usageLimit: number;
}

export interface CreateArtProvenanceInput {
  recipient: string;
  artist: string;
  baseArtworkURI: string;
  layerURIs: string[];
  layerDescriptions: string[];
  revealTimes: Date[];
}

export interface CreateVotingCapsuleInput {
  voter: string;
  proposalTitle: string;
  options: string[];
  endTime: Date;
  voteWeight: number;
}

export interface CreateMusicCapsuleInput {
  recipient: string;
  trackTitle: string;
  artist: string;
  encryptedTrackURI: string;
  previewURI: string;
  releaseDate: Date;
}

export interface CreateAchievementCapsuleInput {
  player: string;
  achievementName: string;
  tiers: string[];
  requirements: number[];
  rewards: string[];
}

export interface CreateEducationalCapsuleInput {
  student: string;
  courseName: string;
  modules: string[];
  unlockDays: number[];
  contentURIs: string[];
}

export interface CreateMemoryCapsuleInput {
  recipient: string;
  title: string;
  encryptedMemoryURI: string;
  unlockTime: Date;
  inheritors: string[];
}

// zkML Types
export interface ZKMLCapsuleConfig {
  modelFile: File | Buffer;
  modelName: string;
  description: string;
  inputSchema: {
    shape: number[];
    dataType: 'float32' | 'int32' | 'uint8';
  };
  outputSchema: {
    shape: number[];
    dataType: 'float32' | 'int32' | 'uint8';
  };
  authorizedAddresses?: string[];
  inputFile?: File | Buffer; // Optional: for EZKL Hub compilation
}

export interface ZKMLCapsule {
  id: number;
  modelHash: string;
  creator: string;
  inputSchemaHash: string;
  outputSchemaHash: string;
  verificationCount: number;
  createdAt: number;
  isActive: boolean;
}

export interface ZKMLVerificationRecord {
  prover: string;
  inputHash: string;
  outputHash: string;
  timestamp: number;
  gasUsed: number;
}

export interface ZKMLManifest {
  version: string;
  capsuleId: number;
  modelHash: string;
  modelName: string;
  description: string;
  inputSchema: {
    shape: number[];
    dataType: 'float32' | 'int32' | 'uint8';
  };
  outputSchema: {
    shape: number[];
    dataType: 'float32' | 'int32' | 'uint8';
  };
  verifier: {
    contractAddress: string;
    chainId: number;
    proofSystem: 'ezkl';
  };
  metadata: {
    creator: string;
    createdAt: string;
    tags: string[];
    ezklArtifactId?: string; // EZKL Hub artifact ID for proof generation
  };
}

export interface ZKMLInferenceResult {
  output: number[];
  proof: string;
  outputHash: string;
  inputHash: string;
  executionTime: number;
} 